import { useState } from "react";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "@/lib/axios";
import { useTutorial } from "@/hooks/queries/useTutorialQueries";
import QuizComponent from "@/components/quiz/QuizComponent";
import QuizProgress from "@/components/quiz/QuizProgress";
import SlidesViewer from "./SlidesViewer";
import { useAuth } from "@/hooks/useAuth";
import { getLoginType } from "@/utils/secureStorage";
import Button from "@/components/common/Button";

interface TutorialModalProps {
  selectedTutorial: any;
  onClose: () => void;
  language: string;
  // Quiz-related props
  showQuiz: boolean;
  quiz: any;
  quizAnswers: any[];
  currentQuizQuestionIdx: number;
  isSubmittingQuiz: boolean;
  quizError: string | null;
  quizResult: any;
  progress: any;
  onAnswerChange: (questionIdx: number, answer: string) => void;
  onPreviousQuestion: () => void;
  onNextQuestion: () => void;
  onQuizSubmitClick: () => void;
  onTakeQuiz: () => void;
}

const TutorialModal = ({
  selectedTutorial,
  onClose,
  language,
  // Quiz-related props
  showQuiz,
  quiz,
  quizAnswers,
  currentQuizQuestionIdx,
  isSubmittingQuiz,
  quizError,
  quizResult,
  progress,
  onAnswerChange,
  onPreviousQuestion,
  onNextQuestion,
  onQuizSubmitClick,
  onTakeQuiz,
}: TutorialModalProps) => {
  const { t } = useTranslation();
  const { currentUser: user, logoutUser } = useAuth();
  const [showFullScreen, setShowFullScreen] = useState(false);

  const {
    data: fetchedTutorial,
    isLoading: isTutorialLoading,
    error: tutorialError,
  } = useTutorial(selectedTutorial?._id || "");


  if (!selectedTutorial) return null;

  const tutorialTitle = (selectedTutorial.title as any)?.[language] || selectedTutorial.title?.en || "";

  // Use the onTakeQuiz prop from parent instead of local function
  // This ensures we use the correct quiz data and logic from CourseDetails

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-2 sm:p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className="relative bg-white dark:bg-gray-900 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden mx-2 sm:mx-4">
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1 mr-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white truncate">
                  {tutorialTitle}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  {t("courses.tutorial")}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(95vh-100px)] sm:max-h-[calc(90vh-120px)]">
            {isTutorialLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent"></div>
              </div>
            ) : tutorialError ? (
              <div className="text-center py-12">
                {(() => {
                  // Check if user is a company admin trying to access tutorial
                  const loginType = getLoginType();
                  const isCompanyAdmin = loginType === "company" || user?.role === "company_admin";
                  
                  if (isCompanyAdmin) {
                    return (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t("auth.accessRestricted")}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                          {t("auth.loginAsUserPlease")}
                        </p>
                        <Button
                          variant="primary"
                          onClick={() => {
                            onClose();
                            logoutUser();
                          }}
                        >
                          {t("auth.logoutAndLoginAsUser")}
                        </Button>
                      </div>
                    );
                  }
                  
                  return <p className="text-red-600 dark:text-red-400">{t("common.error")}</p>;
                })()}
              </div>
            ) : fetchedTutorial ? (
              <>
                {/* Quiz Result */}
                {quizResult && !showQuiz && (
                  <QuizComponent
                    quiz={quiz}
                    quizAnswers={quizAnswers}
                    currentQuizQuestionIdx={currentQuizQuestionIdx}
                    isSubmittingQuiz={isSubmittingQuiz}
                    quizError={quizError}
                    quizResult={quizResult}
                    onAnswerChange={onAnswerChange}
                    onPreviousQuestion={onPreviousQuestion}
                    onNextQuestion={onNextQuestion}
                    onQuizSubmit={onQuizSubmitClick}
                    tutorialTitle={(selectedTutorial.title as any)?.[language] || selectedTutorial.title?.en || "Tutorial"}
                    language={language}
                  />
                )}

                {/* Quiz Component */}
                {showQuiz && quiz && (
                  <QuizComponent
                    quiz={quiz}
                    quizAnswers={quizAnswers}
                    currentQuizQuestionIdx={currentQuizQuestionIdx}
                    isSubmittingQuiz={isSubmittingQuiz}
                    quizError={quizError}
                    quizResult={quizResult}
                    onAnswerChange={onAnswerChange}
                    onPreviousQuestion={onPreviousQuestion}
                    onNextQuestion={onNextQuestion}
                    onQuizSubmit={onQuizSubmitClick}
                    tutorialTitle={(selectedTutorial.title as any)?.[language] || selectedTutorial.title?.en || "Tutorial"}
                    language={language}
                  />
                )}

                {/* Video/Interactive Content */}
                {!showQuiz && !quizResult && (
                  <>
                    {/* Fullscreen Video */}
                    {showFullScreen && (
                      <div className="fixed inset-0 z-50 bg-black flex flex-col">
                        <div className="flex items-center justify-between p-4 bg-gray-900">
                          <h3 className="text-white font-semibold">
                            {tutorialTitle}
                          </h3>
                          <button
                            onClick={() => setShowFullScreen(false)}
                            className="flex items-center px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                            Exit Fullscreen
                          </button>
                        </div>
                        <iframe
                          src={
                            (fetchedTutorial.videoUrl as any)?.[language] ||
                            (fetchedTutorial.videoUrl as any)?.en
                          }
                          className="flex-grow w-full"
                          allowFullScreen
                          title="Interactive Tutorial"
                        />
                      </div>
                    )}

                    {/* Video Preview */}
                    {!showFullScreen && (
                      <div className="mb-6 sm:mb-8">
                        {(fetchedTutorial.videoUrl as any)?.[language]?.endsWith(".html") ||
                        (fetchedTutorial.videoUrl as any)?.[language]?.includes("html") ||
                        (fetchedTutorial.videoUrl as any)?.[language]?.includes("/story") ||
                        (fetchedTutorial.videoUrl as any)?.[language]?.endsWith("/story") ||
                        (fetchedTutorial.videoUrl as any)?.[language]?.includes("netlify.app") ||
                        (fetchedTutorial as any).htmlContent ||
                        (fetchedTutorial as any).interactiveContent ||
                        (fetchedTutorial as any).guidanceContent ? (
                          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center">
                            <div className="mb-3 sm:mb-4">
                              <svg
                                className="w-12 h-12 sm:w-16 sm:h-16 text-white/80 mx-auto mb-3 sm:mb-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                            <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                              {t("courses.interactiveTutorialAvailable")}
                            </h3>
                            <p className="text-sm sm:text-base text-primary-100 mb-4 sm:mb-6">
                              {t("courses.clickToOpenTutorial")}
                            </p>
                            <button
                              className="bg-white text-primary-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg sm:rounded-xl font-semibold text-base sm:text-lg hover:bg-primary-50 transition-all duration-200 transform hover:scale-105 shadow-lg w-full sm:w-auto"
                              onClick={() => setShowFullScreen(true)}
                            >
                              <svg
                                className="w-4 h-4 sm:w-5 sm:h-5 inline mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                                />
                              </svg>
{t("courses.playVideo")}
                            </button>
                          </div>
                        ) : (
                          <div className="bg-gradient-to-r from-gray-900 to-black rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
                            <div className="p-3 sm:p-4 bg-gray-800 border-b border-gray-700">
                              <h3 className="text-white text-sm sm:text-base font-semibold flex items-center">
                                <svg
                                  className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-red-400"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                                Video Tutorial
                              </h3>
                            </div>
                            <video
                              src={
                                (fetchedTutorial.videoUrl as any)?.[language]?.startsWith("http")
                                  ? (fetchedTutorial.videoUrl as any)[language]
                                  : (fetchedTutorial.videoUrl as any)?.en?.startsWith("http")
                                  ? (fetchedTutorial.videoUrl as any).en
                                  : `${API_BASE_URL}/videos/${
                                      (fetchedTutorial.videoUrl as any)?.[language] || (fetchedTutorial.videoUrl as any)?.en
                                    }`
                              }
                              controls
                              autoPlay
                              className="w-full aspect-video bg-black"
                              style={{ minHeight: '200px', maxHeight: '400px' }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Slides */}
                    {fetchedTutorial.slides && fetchedTutorial.slides.length > 0 && (
                      <SlidesViewer
                        slides={fetchedTutorial.slides}
                        language={language}
                        t={t}
                        onTakeQuiz={onTakeQuiz}
                        showTakeQuizButton={!showQuiz && !quizResult}
                      />
                    )}

                    {/* Quiz Progress */}
                    <QuizProgress progress={progress} />

                  </>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialModal;
